import type { WebLocale } from '@/enums/web-locale';
import type { MaybeComputedElementRef, MaybeRefOrGetter } from '@vueuse/core';

import { toValue, useIntersectionObserver, useMounted } from '@vueuse/core';
import { ref, watchEffect } from 'vue';

import { getImpressionPixels } from '@/features/impression-pixels/helpers/getImpressionPixels';

type PixelCallback = (status: 200 | 418) => void;

interface UseImpressionPixelOptions {
	/** Provider short names that will be matched to their pixel in the config. */
	shortNames?: MaybeRefOrGetter<string[] | string | undefined | null>;

	/** Direct pixel url tags to render. */
	pixels?: MaybeRefOrGetter<string[] | string | undefined | null>;

	/**
	 * Callback that fires when the pixel renders.
	 * - on load -> 200
	 * - on error -> 418
	 */
	onEvent?: PixelCallback;

	/**
	 * When the anchor is visible, pixels will be rendered only once.
	 * Default: `true`
	 */
	once?: boolean;

	/**
	 * When false, pixels will not be rendered.
	 * Default: `true`
	 */
	enabled?: MaybeRefOrGetter<boolean>;
}

/**
 * Takes an anchor element on which to watch for visibility and renders the appropriate impression pixels
 * based on the current web locale and an array of provider short names and/or direct impression pixel URLs.
 */
export function useImpressionPixel(
	anchor: MaybeComputedElementRef,
	webLocale: MaybeRefOrGetter<WebLocale>,
	options: UseImpressionPixelOptions & Required<Pick<UseImpressionPixelOptions, 'shortNames'>>
): { stopWatcher: () => void };
export function useImpressionPixel(
	anchor: MaybeComputedElementRef,
	webLocale: MaybeRefOrGetter<WebLocale>,
	options: UseImpressionPixelOptions & Required<Pick<UseImpressionPixelOptions, 'pixels'>>
): { stopWatcher: () => void };
export function useImpressionPixel(
	anchor: MaybeComputedElementRef,
	webLocale: MaybeRefOrGetter<WebLocale>,
	{ shortNames, pixels, onEvent, once = true, enabled = true }: UseImpressionPixelOptions
) {
	const isMounted = useMounted();

	const anchorIsVisible = ref(false);
	const { stop } = useIntersectionObserver(
		anchor,
		([entry]) => {
			anchorIsVisible.value = entry?.isIntersecting || false;
		},
		{ threshold: 0.5 }
	);

	const stopWatcher = watchEffect(() => {
		if (!toValue(enabled) || !isMounted.value || !anchorIsVisible.value) return;

		const pixelUrls = [toValue(pixels) ?? []].flat();

		const shortNameValue = toValue(shortNames);
		if (shortNameValue != null) {
			pixelUrls.push(...getImpressionPixels([shortNameValue].flat(), toValue(webLocale)));
		}

		renderPixels(pixelUrls, onEvent);

		if (once) {
			stopWatcher();
			stop();
		}
	});

	return {
		/**
		 * Call this to stop rendering pixels each time the anchor is visible.
		 * **NOTE**: This is only works if useImpressionPixel is called with `once: false`.
		 */
		stopWatcher,
	};
}

function renderPixels(pixelUrls: string[], onEvent?: PixelCallback) {
	pixelUrls
		.map(pixelUrl => {
			const pixel = new Image(1, 1);

			pixel.src = pixelUrl;
			pixel.alt = 'Advertisement';
			pixel.style.display = 'none';
			pixel.style.border = '0px';

			if (onEvent != null) {
				pixel.onload = () => onEvent(200);
				pixel.onerror = () => onEvent(418);
			}

			return pixel;
		})
		.forEach(pixel => document.body.appendChild(pixel));
}
